import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useSubscription } from "react-apollo-hooks";
import { useMutation } from "@apollo/react-hooks";
// Own components
import LoadingCircleView from "../../Views/LoadingCircleView";
import TicketListView from "../../Views/Tickets/TicketListView";

import { TICKET_REPORTS_CHANGED_SUBSCRIPTION } from "./Subscriptions";
import { ALL_TICKETS_FEED, TICKETS_FEED } from "./Queries";

import useGlobal from "../../store";
import { ticketReportChangedNotification } from "../../utils/snackNotifications";
import { GET_PLAN_FEATURES_APP } from "../Locations/gql/Queries";
import auth from "../../utils/auth";
import history from "../../utils/history";
import moment from "moment";
import {
  GENERATE_TICKET_REPORT_XSL,
  UPDATE_TICKET_MUTATION,
} from "./Mutations";

function Tickets(props) {
  const [session, globalActions] = useGlobal(
    (state) => state.session,
    (actions) => actions
  );
  const { t } = useTranslation(["tickets", "common"]);
  const [ticketsArrayToQuery, setTicketsArrayToQuery] = useState([]);

  const [filters, setFilters] = useState(() => {
    
    let ticketsSelected = sessionStorage.getItem("ticket-ids-selected");
    const out = {
      app: session.user ? session.appRef._id : "",
      skip: 0,
      first: 15,
      user: session.user ? session.user._id : "",
      session: auth.getUserSessionData(session),
      archived: false,
      created_gte: moment().startOf('day').subtract(30, 'days'),
      created_lte: moment().endOf('day'),
      //_id_in: []
    }

    if(ticketsSelected) {
      const selection = JSON.parse(ticketsSelected);
      if(selection?.length) {
        out.ids = selection;
      }
    }
    return out;
  });

  const {
    data: dataTickets,
    error: errorTickets,
    loading: loadingTickets,
    refetch: refetchTickets,
  } = useQuery(TICKETS_FEED, {
    variables: filters,
    fetchPolicy: "network-only",
    nextFetchPolicy: 'cache-first',
  });

  console.log({
    dataTickets
  })

  const { data, error, loading } = useSubscription(
    TICKET_REPORTS_CHANGED_SUBSCRIPTION,
    {
      variables: { where: { app: session.user ? session.appRef._id : "" } },
      onSubscriptionData: ({ client, subscriptionData }) => {
        const { operation, node } = subscriptionData.data.ticketReportChanged;
        ticketReportChangedNotification(t, globalActions, operation, node);
        refetchTickets();
      },
    }
  );

  const { data: dataPlan } = useQuery(GET_PLAN_FEATURES_APP, {
    variables: {
      where: {
        _id: session.user ? session.appRef._id : "",
      },
    },
    fetchPolicy: "network-only",
  });

  const [updateTicket] = useMutation(UPDATE_TICKET_MUTATION);

  const paginationAction = async (values) => {
    setFilters(values);
  };

  const [generateReportMutation] = useMutation(GENERATE_TICKET_REPORT_XSL, {
    onCompleted: (mutationData) => {
      if (mutationData) {
        globalActions.notification.openNotification("tr", {
          message: "Procesando Reporte",
          color: "success",
        });
      }
    },
  });

  useMemo(() => {
    if (
      session.user &&
      !auth.hasPermission("ticketsView", session.permissions)
    ) {
      globalActions.notification.openNotification("tr", {
        message: t("common:common.hasNotPermission"),
        color: "danger",
      });
      history.push("dashboard");
    }
  }, [session]);

  const [isLoadingTickets, setIsLoadinTickets] = useState(false);
  

  const setSelectedTickets = async (arrayIds) => {
    sessionStorage.setItem("ticket-ids-selected", JSON.stringify(arrayIds));
    setFilters(prev => {
      const { ids, ...rest } = prev;
      return arrayIds.length > 0 
        ? { ...rest, ids: arrayIds, skip: 0 }
        : rest;
    });
  }

  const {
    data: allTicketsData,
    error: errorAllTickets,
    loading: loadingAllTickets,
    refetch: refetchAllTickets,
  } = useQuery(ALL_TICKETS_FEED, {
    variables: {
      app: session.user ? session.appRef._id : "",
      user: session.user ? session.user._id : "",
      session: auth.getUserSessionData(session),
      archived: filters.archived,
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: 'cache-first'
  });

  useEffect(() => {
    async function fetchTicketsFeed() {
      setIsLoadinTickets(true)
      await refetchTickets(filters);
      setIsLoadinTickets(false)
    }
    fetchTicketsFeed()
  }, [filters]);

  if (loadingAllTickets) {
    return <LoadingCircleView />;
  } else {
    return (
      <TicketListView
        plan={dataPlan?.getPlanFeaturesApp || null}
        tickets={dataTickets?.tickets}
        allTickets={allTicketsData.tickets}
        paginationAction={paginationAction}
        filters={filters}
        generateReport={({ from, to }) => {
          const variables = {
            data: {
              app: session.appRef._id,
              session: {
                user: {
                  email: session.user.email,
                  firstName: session.user.firstName,
                  lastName: session.user.lastName,
                  _id: session.user._id,
                },
              },
              dateRange: {
                from: from, //moment(from).utcOffset(0, true).format(),
                to: to, //moment(to).utcOffset(0, true).format()
              },
              query: {
                timezone: moment.tz.guess(),
              },
              _id_in: filters.ids
            },
          };

          generateReportMutation({
            variables,
          });
        }}
        archive={(ticket) => {
          const validTicketSegmentAssignConfigs = ticket.ticketSegmentAssignConfigs
            .filter((assign) => {
              return assign.segment && assign.userMobile;
            })
            .map((assign) => {
              return {
                segmentId: assign.segment._id,
                userMobileId: assign.userMobile._id,
              };
            });

          const variables = {
            data: {
              app: ticket.app,
              name: ticket.name,
              description: ticket.description,
              emails: {
                set: ticket.emails,
              },
              triggers: {
                connect: ticket.triggers.map((trigger) => ({
                  _id: trigger._id,
                })),
              },
              finisher: {
                connect: {
                  _id: ticket.finisher._id,
                },
              },
              ticketSegmentAssignConfigs: validTicketSegmentAssignConfigs,
              whoResolves: ticket.whoResolves,
              segment: ticket.segment,
              archived: false,
              pdfReportConfig: ticket.pdfReportConfig,
              topOffender: ticket.topOffender,
              archived: !ticket.archived,
            },
            where: {
              _id: ticket._id,
              session: auth.getUserSessionData(session),
            },
          };
          updateTicket({
            variables,
          });
          refetchTickets();
        }}
        changeStatus={() => {
          setFilters({
            ...filters,
            archived: !filters.archived
          });
        }}
        setSelectedTickets={setSelectedTickets}
        isLoadingTickets={isLoadingTickets}
      />
    );
  }
}

const styles = (theme) => ({
  center: {
    textAlign: "center",
  },
});

export default Tickets;
